const SIZE_CLASS_TO_PX = {
  'ql-size-6px': '6px',
  'ql-size-8px': '8px',
  'ql-size-10px': '10px',
  'ql-size-12px': '12px',
  'ql-size-15px': '15px',
  'ql-size-16px': '16px',
  'ql-size-18px': '18px',
  'ql-size-24px': '24px',
};

function getElementFontSize(el) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return null;
  for (const cls of el.classList) {
    if (SIZE_CLASS_TO_PX[cls]) return SIZE_CLASS_TO_PX[cls];
  }
  const style = el.getAttribute('style') || '';
  const match = style.match(/font-size\s*:\s*([^;]+)/i);
  if (match) return match[1].trim();
  return null;
}

function findDescendantFontSize(el) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return null;
  const own = getElementFontSize(el);
  if (own) return own;
  const sized = el.querySelector('[class*="ql-size-"], [style*="font-size"]');
  return sized ? getElementFontSize(sized) : null;
}

function findSiblingFontSize(node, direction) {
  const next = direction === 'previous' ? 'previousSibling' : 'nextSibling';
  let sibling = node[next];
  while (sibling) {
    if (sibling.nodeType === Node.ELEMENT_NODE) {
      const size = findDescendantFontSize(sibling);
      if (size) return size;
      // Element sibling without size: stop searching this direction so we
      // don't leak across unrelated runs.
      return null;
    }
    sibling = sibling[next];
  }
  return null;
}

function findAncestorFontSize(el) {
  let current = el?.parentElement;
  while (current) {
    const size = getElementFontSize(current);
    if (size) return size;
    current = current.parentElement;
  }
  return null;
}

export function convertQuillHtmlToInlineAndClassHtml(html, defaultFontSize) {
  const container = document.createElement('div');
  container.innerHTML = html;

  // Wrap any bare text node in a sized span so every text run in the exported
  // HTML carries an explicit font-size. Resolve the size from neighbours first
  // (so adjacent runs match), then ancestors, then the provided default.
  const wrapBareText = (parent) => {
    Array.from(parent.childNodes).forEach((child) => {
      if (
        child.nodeType !== Node.TEXT_NODE ||
        !child.textContent ||
        child.textContent.trim().length === 0
      ) {
        return;
      }
      const size =
        findSiblingFontSize(child, 'previous') ||
        findSiblingFontSize(child, 'next') ||
        findAncestorFontSize(child) ||
        defaultFontSize;
      if (!size) return;
      const span = parent.ownerDocument.createElement('span');
      span.setAttribute('style', `font-size: ${size}`);
      parent.insertBefore(span, child);
      span.appendChild(child);
    });
  };

  const walkForWrap = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    // If this element already provides a font-size, its bare text children
    // inherit it via CSS — no need to wrap (avoids redundant nesting).
    if (!getElementFontSize(node)) {
      wrapBareText(node);
    }
    Array.from(node.childNodes).forEach(walkForWrap);
  };
  container.childNodes.forEach(walkForWrap);

  const fontClassToFamily = {
    'ql-font-monospace': 'monospace',
    'ql-font-montserrat': 'montserrat',
    'ql-font-roboto': 'roboto',
    'ql-font-arial': 'arial',
    'ql-font-serif': 'serif',
  };

  const sizeClassToPx = SIZE_CLASS_TO_PX;

  function processElement(el) {
    if (el.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = el.tagName.toUpperCase();
    const classes = Array.from(el.classList);
    const styleMap = new Map();

    // Handle font and size classes
    const classesToRemove = [];

    classes.forEach((cls) => {
      if (fontClassToFamily[cls]) {
        styleMap.set('font-family', fontClassToFamily[cls]);
        classesToRemove.push(cls);
      }
      if (sizeClassToPx[cls]) {
        styleMap.set('font-size', sizeClassToPx[cls]);
        classesToRemove.push(cls);
      }
      // Track all Quill-specific classes for removal
      if (cls.startsWith('ql-')) {
        classesToRemove.push(cls);
      }
    });

    // Remove Quill-specific classes from elements
    classesToRemove.forEach((cls) => el.classList.remove(cls));

    // Apply font size from child span to <li> itself
    if (tagName === 'LI' && !styleMap.has('font-size')) {
      const spanWithSize = el.querySelector('[class*="ql-size-"]');
      if (spanWithSize) {
        const spanClasses = Array.from(spanWithSize.classList);
        spanClasses.forEach((cls) => {
          if (sizeClassToPx[cls]) {
            styleMap.set('font-size', sizeClassToPx[cls]);
          }
        });
      }
    }

    // Handle OL styling
    if (tagName === 'OL') {
      styleMap.set('list-style-type', 'decimal');
      styleMap.set('padding-left', '20px');
    }

    // Handle LI in OL with data-list="bullet"
    if (
      tagName === 'LI' &&
      el.getAttribute('data-list') === 'bullet' &&
      el.parentElement?.tagName.toUpperCase() === 'OL'
    ) {
      styleMap.set('list-style-type', 'disc');
    }

    // Preserve existing inline styles
    const inlineStyle = el.getAttribute('style') || '';
    const newStyleParts = [...styleMap.entries()].map(
      ([key, val]) => `${key}:${val}`
    );
    const finalStyle = [...newStyleParts, inlineStyle]
      .filter(Boolean)
      .join('; ');

    if (finalStyle) {
      el.setAttribute('style', finalStyle);
    }
  }

  const walk = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      processElement(node);
      node.childNodes.forEach(walk);
    }
  };

  container.childNodes.forEach(walk);

  // remove cursor
  const htmlString = container.innerHTML;
  const cleaned = htmlString.replace(
    /<span class="ql-cursor">.*?<\/span>/g,
    ''
  );

  return cleaned;
}
